import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';
import JapanAddressSelector from './JapanAddressSelector';
import TalentBillingChart from './TalentBillingChart';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import ClientSelectionFields from './ClientSelectionFields';
import { useFilePreview } from '../hooks/useFilePreview';
import { getCurrentResidenceStatus, isSpecifiedSkill2Status, sortRenewalEntriesNewestFirst } from '../utils/residenceStatus';

// 可复用的表单项和详情项组件 - 横向布局（左边字段名，右边字段内容）
const DetailItem = ({ label, value }) => (
    <div className="grid grid-cols-5 gap-4 py-3 border-b border-gray-100">
        <dt className="col-span-2 text-sm font-medium text-gray-500 flex items-center">{label}</dt>
        <dd className="col-span-3 text-sm text-gray-900 flex items-center">{value || 'N/A'}</dd>
    </div>
);

const EditItem = ({ label, name, value, onChange, type = 'text', inputMode, pattern, maxLength, disabled = false }) => {
    // Set date range limits for date inputs to restrict year to 4 digits
    const dateProps = type === 'date' ? {
        min: '1900-01-01',
        max: '2099-12-31'
    } : {};

    return (
        <div className="grid grid-cols-5 gap-4 py-3 border-b border-gray-100">
            <label htmlFor={name} className="col-span-2 text-sm font-medium text-gray-700 flex items-center">{label}</label>
            <div className="col-span-3">
                <input
                    type={type}
                    name={name}
                    id={name}
                    value={value || ''}
                    onChange={onChange}
                    inputMode={inputMode}
                    pattern={pattern}
                    maxLength={maxLength}
                    disabled={disabled}
                    {...dateProps}
                    className={`block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                />
            </div>
        </div>
    );
};

const SelectItem = ({ label, name, value, onChange, onInput, required = false, options, optionType, t, getTranslatedOption, disabled = false }) => (
    <div className="grid grid-cols-5 gap-4 py-3 border-b border-gray-100">
        <label htmlFor={name} className="col-span-2 text-sm font-medium text-gray-700 flex items-center">
            {label}{required && '*'}
        </label>
        <div className="col-span-3">
            <select
                name={name}
                id={name}
                value={value || ''}
                onChange={onChange}
                onInput={onInput}
                required={required}
                disabled={disabled}
                className={`block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
            >
                <option value="" disabled>{t('pleaseSelect')}</option>
                {options.map(option => (
                    <option key={option} value={option}>
                        {getTranslatedOption ? getTranslatedOption(option, optionType) : option}
                    </option>
                ))}
            </select>
        </div>
    </div>
);

const isImage = (fileType) => fileType.startsWith('image/');

const normalizePauseDate = (value) => {
    if (!value) return '';
    if (typeof value === 'string') {
        return value.split('T')[0];
    }
    return value;
};

const mapPauseRowsFromApi = (rows, talentSnapshot) => {
    const mapped = (Array.isArray(rows) ? rows : []).map((row, index) => ({
        id: row.id || `pause-existing-${index}`,
        pauseType: row.pauseType || "育休",
        startDate: normalizePauseDate(row.startDate),
        endDate: normalizePauseDate(row.endDate)
    }));

    if (mapped.length > 0) {
        return mapped;
    }

    const snapshotType = (talentSnapshot?.employmentPauseType || '').trim();
    if ((snapshotType === '育休' || snapshotType === '一時帰国') && talentSnapshot?.employmentPauseStartDate) {
        return [{
            id: 'pause-legacy',
            pauseType: snapshotType,
            startDate: normalizePauseDate(talentSnapshot.employmentPauseStartDate),
            endDate: normalizePauseDate(talentSnapshot.employmentPauseEndDate)
        }];
    }

    return [];
};

export default function SalesResidenceInfoModal({ isOpen, onClose, talent }) {
    const { t } = useTranslation();
    const attachmentPreview = useFilePreview(apiService.downloadAttachment);
    const [talentFormData, setTalentFormData] = useState({});
    const [employmentPauseHistory, setEmploymentPauseHistory] = useState([]);
    const [residenceData, setResidenceData] = useState({});
    const [editableResidenceDates, setEditableResidenceDates] = useState({ startDate: '', endDate: '' });
    const [filesToUpload, setFilesToUpload] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]); // 临时选中的文件
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [thumbnailUrls, setThumbnailUrls] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [customFieldValue, setCustomFieldValue] = useState('');
    const [showCustomField, setShowCustomField] = useState(false);

    // Photo-specific state
    const [photoToUpload, setPhotoToUpload] = useState(null);
    const [existingPhoto, setExistingPhoto] = useState(null);
    const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
    const attachmentRequestRef = useRef(0);
    const residenceRequestRef = useRef(0);

    // Options for dropdowns - same as CreateOrEditTalentModal
    const genderOptions = useMemo(() => ["男", "女"], []);
    const nationalityOptions = useMemo(() => ["中国", "韩国", "缅甸", "印度尼西亚", "越南", "菲律宾"], []);
    const fieldOptions = useMemo(() => ["外食", "飲食料品製造", "溶接", "介護", "他"], []);
    const employmentStatusOptions = useMemo(() => ["内定", "申請中", "在職", "離職", "待入国"], []);
    const billingPlanTypeOptions = useMemo(() => ["通常", "初期費+Nヶ月管理費", "段階管理費"], []);
    const pauseTypeOptions = useMemo(() => ["育休", "一時帰国"], []);
    const createPauseItem = () => ({
        id: `pause-${Date.now()}-${Math.random()}`,
        pauseType: "育休",
        startDate: '',
        endDate: ''
    });

    const sanitizePauseRows = (rows) => {
        return (Array.isArray(rows) ? rows : [])
            .map(row => ({
                pauseType: (row.pauseType || '').trim(),
                startDate: normalizePauseDate(row.startDate),
                endDate: normalizePauseDate(row.endDate) || null
            }))
            .filter(row => (row.pauseType === '育休' || row.pauseType === '一時帰国') && row.startDate);
    };

    const resolveCurrentPauseSnapshot = (rows, employmentStatus) => {
        if (employmentStatus !== '在職') {
            return { pauseType: 'なし', startDate: null, endDate: null };
        }

        const today = new Date().toISOString().slice(0, 10);
        const activeRow = rows
            .filter(row => row.startDate && row.startDate <= today)
            .filter(row => !row.endDate || row.endDate >= today)
            .sort((a, b) => (a.startDate < b.startDate ? 1 : -1))[0];

        if (!activeRow) {
            return { pauseType: 'なし', startDate: null, endDate: null };
        }

        return {
            pauseType: activeRow.pauseType,
            startDate: activeRow.startDate,
            endDate: activeRow.endDate || null
        };
    };

    // Translation mapping for display - same as CreateOrEditTalentModal
    const getTranslatedOption = (value, type) => {
        const translations = {
            gender: { "男": t('male'), "女": t('female') },
            nationality: {
                "中国": t('chinese'),
                "韩国": t('korean'),
                "韓国": t('korean'),
                "缅甸": t('myanmar'),
                "ミャンマー": t('myanmar'),
                "印度尼西亚": t('indonesia'),
                "インドネシア": t('indonesia'),
                "越南": t('vietnamese'),
                "ベトナム": t('vietnamese'),
                "台湾": t('taiwan'),
                "菲律宾": t('filipino'),
                "フィリピン": t('filipino')
            },
            field: {
                "外食": t('restaurant'),
                "飲食料品製造": t('foodManufacturing'),
                "溶接": t('welding'),
                "介護": t('care'),
                "他": t('otherField')
            },
            employmentStatus: {
                "内定": t('jobOffer'),
                "申請中": t('applying'),
                "在職": t('employed'),
                "離職": t('unemployed'),
                "待入国": t('awaitingEntry')
            },
            employmentPauseType: {
                "なし": t('employmentPauseTypeNone'),
                "育休": t('employmentPauseTypeChildcare'),
                "一時帰国": t('employmentPauseTypeTemporaryReturn')
            },
            billingPlanType: {
                "通常": t('billingPlanTypeNormal'),
                "初期費+Nヶ月管理費": t('billingPlanTypeInitialPlus'),
                "段階管理費": t('billingPlanTypeTiered')
            }
        };
        return translations[type]?.[value] || value;
    };

    const fetchAttachments = useCallback(async (talentId) => {
        if (!talentId) return;
        const requestId = ++attachmentRequestRef.current;
        try {
            // Clear stale photo preview before loading new talent attachments
            setExistingPhoto(null);
            setPhotoToUpload(null);
            setPhotoPreviewUrl(prevUrl => {
                if (prevUrl) {
                    URL.revokeObjectURL(prevUrl);
                }
                return null;
            });

            const attachmentsData = await apiService.getAttachmentsForTalent(talentId);
            if (attachmentRequestRef.current !== requestId) {
                return;
            }
            const salesAttachments = attachmentsData.filter(att => att.sourceModule === "Sales");
            const photoAttachments = attachmentsData.filter(att => att.sourceModule === "Photo");

            setExistingAttachments(salesAttachments);

            // Handle existing photo
            if (photoAttachments.length > 0) {
                const photo = photoAttachments[0]; // Use first photo
                setExistingPhoto(photo);
                // Load photo preview
                try {
                    const blob = await apiService.downloadAttachment(photo.id);
                    if (attachmentRequestRef.current !== requestId) {
                        return;
                    }
                    const url = URL.createObjectURL(blob);
                    setPhotoPreviewUrl(url);
                } catch (err) {
                    if (attachmentRequestRef.current !== requestId) {
                        return;
                    }
                    console.error('Failed to load photo:', err);
                    setExistingPhoto(null);
                    setPhotoPreviewUrl(null);
                }
            } else {
                if (attachmentRequestRef.current !== requestId) {
                    return;
                }
                setExistingPhoto(null);
                setPhotoPreviewUrl(null);
            }

            const newThumbnailUrls = {};
            for (const att of salesAttachments) {
                if (isImage(att.fileType)) {
                    const blob = await apiService.downloadAttachment(att.id);
                    if (attachmentRequestRef.current !== requestId) {
                        return;
                    }
                    newThumbnailUrls[att.id] = URL.createObjectURL(blob);
                }
            }
            if (attachmentRequestRef.current !== requestId) {
                Object.values(newThumbnailUrls).forEach(URL.revokeObjectURL);
                return;
            }
            setThumbnailUrls(newThumbnailUrls);
        } catch (err) {
            if (attachmentRequestRef.current === requestId) {
                setError(t('loadFailed') + ': ' + err.message);
            }
        }
    }, [t]);

    const fetchResidenceData = useCallback(async (talentId) => {
        if (!talentId) return;
        const requestId = ++residenceRequestRef.current;
        setIsLoading(true);
        setError('');
        try {
            console.log('Starting to fetch data for talent:', talentId);

            // 获取人才的详细信息（包括sales可编辑的字段和在留信息）
            console.log('Fetching talent details...');
            const talentDetails = await apiService.getTalentById(talentId);
            if (residenceRequestRef.current !== requestId) {
                return;
            }
            console.log('Talent details received:', talentDetails);
            setTalentFormData(talentDetails ? {
                ...talentDetails,
                billingPlanType: talentDetails.billingPlanType || "通常",
                employmentPauseType: talentDetails.employmentPauseType || "なし",
                initialCostNotCharged: Boolean(talentDetails.initialCostNotCharged),
                managementFeeNotCharged: Boolean(talentDetails.managementFeeNotCharged)
            } : {});

            const pauseData = await apiService.getTalentEmploymentPauses(talentId).catch(() => ({ pauses: [] }));
            if (residenceRequestRef.current !== requestId) {
                return;
            }
            setEmploymentPauseHistory(mapPauseRowsFromApi(pauseData?.pauses, talentDetails));

            // 检查分野字段是否是自定义值
            const standardFields = ["外食", "飲食料品製造", "溶接", "介護", "他"];
            if (talentDetails?.field && !standardFields.includes(talentDetails.field)) {
                console.log('Detected custom field value:', talentDetails.field);
                setShowCustomField(true);
                setCustomFieldValue(talentDetails.field);
            } else {
                setShowCustomField(false);
                setCustomFieldValue('');
            }

            // 直接从talentDetails中提取在留信息，不需要单独API调用
            console.log('Extracting residence data from talent details...');
            const residenceInfo = talentDetails?.residencePermit || talentDetails?.ResidencePermit || {};
            console.log('Residence data extracted:', residenceInfo);
            setResidenceData(residenceInfo);
            setEditableResidenceDates({
                startDate: normalizePauseDate(residenceInfo.startDate || residenceInfo.StartDate),
                endDate: normalizePauseDate(residenceInfo.endDate || residenceInfo.EndDate)
            });
        } catch (err) {
            console.error('Error fetching data:', err);
            console.error('Error details:', err.message, err.stack);
            if (residenceRequestRef.current === requestId) {
                setError(t('fetchFailed') + ': ' + err.message);
            }
        } finally {
            if (residenceRequestRef.current === requestId) {
                setIsLoading(false);
            }
        }
    }, [t]);

    useEffect(() => {
        if (isOpen && talent?.id) {
            console.log('useEffect triggered: isOpen =', isOpen, ', talent.id =', talent.id);
            setFilesToUpload([]);
            setError('');
            fetchResidenceData(talent.id);
            fetchAttachments(talent.id);
        }
    }, [isOpen, talent?.id, fetchResidenceData, fetchAttachments]);

    // Handle custom field setup when talentFormData changes
    useEffect(() => {
        if (talentFormData.field && !fieldOptions.includes(talentFormData.field)) {
            setShowCustomField(true);
            setCustomFieldValue(talentFormData.field);
        } else {
            setShowCustomField(false);
            setCustomFieldValue('');
        }
    }, [talentFormData.field, fieldOptions]);

    // Cleanup thumbnails when component unmounts or thumbnails change
    useEffect(() => {
        return () => {
            Object.values(thumbnailUrls).forEach(URL.revokeObjectURL);
        };
    }, [thumbnailUrls]);

    // Cleanup photo preview URL on unmount
    useEffect(() => {
        return () => {
            if (photoPreviewUrl) {
                URL.revokeObjectURL(photoPreviewUrl);
            }
        };
    }, [photoPreviewUrl]);

    // Photo download handler
    const handleDownloadPhoto = async () => {
        if (existingPhoto) {
            try {
                const blob = await apiService.downloadAttachment(existingPhoto.id);
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = existingPhoto.fileName || 'photo.jpg';
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } catch (err) {
                setError(t('downloadFailed') + ': ' + err.message);
            }
        }
    };

    // Photo handlers
    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError(t('onlyImagesAllowed') || '画像ファイルのみアップロード可能です');
                return;
            }

            // Validate file size (e.g., max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError(t('fileTooLarge') || 'ファイルサイズが大きすぎます（最大5MB）');
                return;
            }

            setPhotoToUpload(file);

            // Create preview URL
            const url = URL.createObjectURL(file);
            if (photoPreviewUrl) {
                URL.revokeObjectURL(photoPreviewUrl);
            }
            setPhotoPreviewUrl(url);
        }
    };

    const handleDeletePhoto = async () => {
        if (existingPhoto && window.confirm(t('confirmDelete'))) {
            try {
                await apiService.deleteAttachment(existingPhoto.id);
                setExistingPhoto(null);
                if (photoPreviewUrl) {
                    URL.revokeObjectURL(photoPreviewUrl);
                }
                setPhotoPreviewUrl(null);
            } catch (err) {
                setError(t('deleteFailed') + ': ' + err.message);
            }
        } else if (photoToUpload) {
            // Just clear the selected photo
            setPhotoToUpload(null);
            if (photoPreviewUrl) {
                URL.revokeObjectURL(photoPreviewUrl);
            }
            setPhotoPreviewUrl(null);
        }
    };

    const handleResidenceDateChange = (e) => {
        const { name, value } = e.target;
        setEditableResidenceDates(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSaveTalentInfo = async () => {
        setIsLoading(true);
        setError('');
        try {
            // Clean data similar to CreateOrEditTalentModal
            const cleanedData = { ...talentFormData };

            // Remove navigation properties
            delete cleanedData.salesStaff;
            delete cleanedData.residencePermit;
            delete cleanedData.loan;
            delete cleanedData.attachments;
            delete cleanedData.employmentPauses;
            delete cleanedData.clientOrganization;
            delete cleanedData.clientWorkplace;

            // Handle date fields
            if (!cleanedData.dateOfBirth || cleanedData.dateOfBirth === '') {
                cleanedData.dateOfBirth = null;
            }
            if (!cleanedData.contractEndDate || cleanedData.contractEndDate === '') {
                cleanedData.contractEndDate = null;
            }

            // Ensure numeric fields are numbers
            cleanedData.initialCostNotCharged = Boolean(cleanedData.initialCostNotCharged);
            cleanedData.managementFeeNotCharged = Boolean(cleanedData.managementFeeNotCharged);

            if (cleanedData.initialCostNotCharged || cleanedData.initialCost === '' || cleanedData.initialCost === null || cleanedData.initialCost === undefined) {
                cleanedData.initialCost = null;
            } else {
                cleanedData.initialCost = parseFloat(cleanedData.initialCost);
                if (Number.isNaN(cleanedData.initialCost)) {
                    cleanedData.initialCost = null;
                }
            }
            if (cleanedData.introductionFee === '' || cleanedData.introductionFee === null || cleanedData.introductionFee === undefined) {
                cleanedData.introductionFee = null;
            } else {
                cleanedData.introductionFee = parseFloat(cleanedData.introductionFee);
                if (Number.isNaN(cleanedData.introductionFee)) {
                    cleanedData.introductionFee = null;
                }
            }
            if (cleanedData.managementFeeNotCharged) {
                cleanedData.managementFee = 0;
                cleanedData.firstMonthManagementFee = null;
                cleanedData.includedManagementMonths = null;
                cleanedData.tier1Months = null;
                cleanedData.tier1ManagementFee = null;
                cleanedData.tier2Months = null;
                cleanedData.tier2ManagementFee = null;
                cleanedData.billingPlanType = '通常';
            } else {
                cleanedData.managementFee = parseFloat(cleanedData.managementFee) || 0;
                if (cleanedData.firstMonthManagementFee) {
                    cleanedData.firstMonthManagementFee = parseFloat(cleanedData.firstMonthManagementFee) || 0;
                } else {
                    cleanedData.firstMonthManagementFee = null;
                }
            }

            const parseIntOrNull = (value) => {
                if (value === '' || value === null || value === undefined) return null;
                const parsed = parseInt(value, 10);
                return Number.isNaN(parsed) ? null : parsed;
            };
            const parseDecimalOrNull = (value) => {
                if (value === '' || value === null || value === undefined) return null;
                const parsed = parseFloat(value);
                return Number.isNaN(parsed) ? null : parsed;
            };

            cleanedData.includedManagementMonths = parseIntOrNull(cleanedData.includedManagementMonths);
            cleanedData.tier1Months = parseIntOrNull(cleanedData.tier1Months);
            cleanedData.tier2Months = parseIntOrNull(cleanedData.tier2Months);
            cleanedData.tier1ManagementFee = parseDecimalOrNull(cleanedData.tier1ManagementFee);
            cleanedData.tier2ManagementFee = parseDecimalOrNull(cleanedData.tier2ManagementFee);

            const normalizedPauseRows = sanitizePauseRows(employmentPauseHistory);
            const invalidPause = normalizedPauseRows.find(row => row.endDate && row.endDate < row.startDate);
            if (invalidPause) {
                throw new Error(t('employmentPauseDateRangeError'));
            }

            // Ensure employmentStatus has value
            if (!cleanedData.employmentStatus || cleanedData.employmentStatus === '') {
                cleanedData.employmentStatus = '在職';
            }
            if (!cleanedData.billingPlanType || cleanedData.billingPlanType === '') {
                cleanedData.billingPlanType = '通常';
            }
            const currentPauseSnapshot = resolveCurrentPauseSnapshot(normalizedPauseRows, cleanedData.employmentStatus);
            cleanedData.employmentPauseType = currentPauseSnapshot.pauseType;
            cleanedData.employmentPauseStartDate = currentPauseSnapshot.startDate;
            cleanedData.employmentPauseEndDate = currentPauseSnapshot.endDate;

            const normalizedResidenceDatePayload = {
                startDate: editableResidenceDates.startDate || null,
                endDate: editableResidenceDates.endDate || null
            };
            const shouldUpdateResidenceDates =
                normalizePauseDate(residenceData.startDate) !== (normalizedResidenceDatePayload.startDate || '') ||
                normalizePauseDate(residenceData.endDate) !== (normalizedResidenceDatePayload.endDate || '');

            if (
                normalizedResidenceDatePayload.startDate &&
                normalizedResidenceDatePayload.endDate &&
                normalizedResidenceDatePayload.endDate < normalizedResidenceDatePayload.startDate
            ) {
                throw new Error('退職日は入職日より前に設定できません。');
            }

            await apiService.updateTalent(talent.id, cleanedData);
            await apiService.updateTalentEmploymentPauses(talent.id, normalizedPauseRows);

            if (shouldUpdateResidenceDates) {
                const updatedResidenceDates = await apiService.updateResidenceEmploymentDates(talent.id, normalizedResidenceDatePayload);
                setResidenceData(prev => ({
                    ...prev,
                    id: updatedResidenceDates.residencePermitId || prev.id,
                    startDate: updatedResidenceDates.startDate,
                    endDate: updatedResidenceDates.endDate,
                    updatedAt: updatedResidenceDates.updatedAt,
                    residenceInfoUpdatedAt: updatedResidenceDates.residenceInfoUpdatedAt ?? prev.residenceInfoUpdatedAt
                }));
                setEditableResidenceDates({
                    startDate: normalizePauseDate(updatedResidenceDates.startDate),
                    endDate: normalizePauseDate(updatedResidenceDates.endDate)
                });
            }

            // Upload photo first if selected
            if (photoToUpload) {
                // Delete existing photo first if exists
                if (existingPhoto) {
                    await apiService.deleteAttachment(existingPhoto.id);
                }
                // Upload new photo with "Photo" module type
                await apiService.uploadAttachment(talent.id, photoToUpload, "Photo");
            }

            // Upload new attachments
            if (filesToUpload.length > 0) {
                for (const file of filesToUpload) {
                    await apiService.uploadAttachment(talent.id, file, "Sales");
                }
            }

            alert(t('saveSuccess') || '保存しました');
            onClose();
        } catch(e) {
            setError(e.message || t('saveFailed'));
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTalentChange = (e) => {
        const { name, value, type, checked } = e.target;
        const resolvedValue = type === 'checkbox' ? checked : value;
        console.log('[SalesResidenceInfoModal] handleTalentChange:', name, '=', value, ', showCustomField=', showCustomField);
        if (name === 'initialCostNotCharged') {
            const nextData = { ...talentFormData, initialCostNotCharged: Boolean(resolvedValue) };
            if (nextData.initialCostNotCharged) {
                nextData.initialCost = '';
            }
            setTalentFormData(nextData);
            return;
        }
        if (name === 'managementFeeNotCharged') {
            const nextData = { ...talentFormData, managementFeeNotCharged: Boolean(resolvedValue) };
            if (nextData.managementFeeNotCharged) {
                nextData.managementFee = 0;
                nextData.firstMonthManagementFee = '';
                nextData.includedManagementMonths = '';
                nextData.tier1Months = '';
                nextData.tier1ManagementFee = '';
                nextData.tier2Months = '';
                nextData.tier2ManagementFee = '';
                nextData.billingPlanType = '通常';
            }
            setTalentFormData(nextData);
            return;
        }
        if (name === 'employmentStatus') {
            const nextData = { ...talentFormData, employmentStatus: value };
            if (value !== '在職') {
                nextData.employmentPauseType = 'なし';
                nextData.employmentPauseStartDate = '';
                nextData.employmentPauseEndDate = '';
            }
            setTalentFormData(nextData);
            return;
        }
        if (name === 'billingPlanType') {
            if (talentFormData.managementFeeNotCharged) {
                return;
            }
            const nextData = { ...talentFormData, billingPlanType: value };
            if (value === '通常') {
                nextData.includedManagementMonths = '';
                nextData.tier1Months = '';
                nextData.tier1ManagementFee = '';
                nextData.tier2Months = '';
                nextData.tier2ManagementFee = '';
            } else if (value === '初期費+Nヶ月管理費') {
                nextData.tier1Months = '';
                nextData.tier1ManagementFee = '';
                nextData.tier2Months = '';
                nextData.tier2ManagementFee = '';
            } else if (value === '段階管理費') {
                nextData.includedManagementMonths = '';
            }
            setTalentFormData(nextData);
            return;
        }
        if (name === 'managementNumber') {
            const numericValue = (value || '').replace(/\D/g, '');
            setTalentFormData({ ...talentFormData, [name]: numericValue });
            return;
        }
        if (name === 'field') {
            if (value === '他') {
                console.log('[SalesResidenceInfoModal] Setting showCustomField=true');
                setShowCustomField(true);
                setCustomFieldValue('');
                setTalentFormData({ ...talentFormData, [name]: value });
            } else {
                console.log('[SalesResidenceInfoModal] Setting showCustomField=false, value=', value);
                setShowCustomField(false);
                setCustomFieldValue('');
                setTalentFormData({ ...talentFormData, [name]: value });
            }
        } else {
            setTalentFormData({ ...talentFormData, [name]: value });
        }
    };

    const handlePauseRowChange = (id, field, value) => {
        setEmploymentPauseHistory(prev => prev.map(row => (
            row.id === id ? { ...row, [field]: value } : row
        )));
    };

    const handleAddPauseRow = () => {
        setEmploymentPauseHistory(prev => [...prev, createPauseItem()]);
    };

    const handleRemovePauseRow = (id) => {
        setEmploymentPauseHistory(prev => prev.filter(row => row.id !== id));
    };

    const handleCustomFieldChange = (e) => {
        const value = e.target.value;
        setCustomFieldValue(value);
        // 当用户在自定义输入框中输入时，更新field值
        // 即使输入框为空，也保持showCustomField=true，不关闭输入框
        setTalentFormData({ ...talentFormData, field: value || '' });
    };

    const handleClientSelectionChange = (patch) => {
        setTalentFormData(prev => ({
            ...prev,
            ...patch
        }));
    };

    const handleFileChange = (e) => {
        // 只是暂时保存选中的文件，不直接添加到待上传列表
        setSelectedFiles([...e.target.files]);
        e.target.value = '';
    };

    const handleAddFiles = () => {
        // 点击"添加"按钮后，将选中的文件添加到待上传列表
        if (selectedFiles.length > 0) {
            setFilesToUpload([...filesToUpload, ...selectedFiles]);
            setSelectedFiles([]); // 清空临时选择
            // 重置file input
            const fileInput = document.getElementById('file-input-sales');
            if (fileInput) fileInput.value = '';
        }
    };

    const handleRemovePendingFile = (index) => {
        // 从待上传列表中删除文件
        setFilesToUpload(filesToUpload.filter((_, i) => i !== index));
    };

    const handleDeleteAttachment = async (attachmentId) => {
        if (window.confirm(t('confirmDelete'))) {
            try {
                await apiService.deleteAttachment(attachmentId);
                fetchAttachments(talent.id);
            } catch (err) {
                setError(t('deleteFailed') + ': ' + err.message);
            }
        }
    };

    const handleDownload = async (attachmentId, fileName) => {
        try {
            const blob = await apiService.downloadAttachment(attachmentId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message || t('downloadFailed'));
        }
    };

    if (!isOpen) return null;

    // 渲染在留信息（只读显示）
    const renderResidenceInfo = () => (
        <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <h3 className="text-lg font-medium text-blue-900 mb-4">在留情報（入職日・退職日のみ編集可）</h3>
            <div>
                <DetailItem label={t('interviewDate')} value={residenceData.interviewDate ? new Date(residenceData.interviewDate).toLocaleDateString('ja-JP') : 'N/A'} />
                <DetailItem label={t('applicationDate')} value={residenceData.applicationDate ? new Date(residenceData.applicationDate).toLocaleDateString('ja-JP') : 'N/A'} />
                <DetailItem label={t('grantDate')} value={residenceData.grantDate ? new Date(residenceData.grantDate).toLocaleDateString('ja-JP') : 'N/A'} />
                <DetailItem label={t('applicationMethod')} value={residenceData.applicationMethod} />
                <DetailItem label={t('applicationNumber')} value={residenceData.applicationNumber} />
                <DetailItem label="在留資格" value={residenceData.residenceStatus} />
                <DetailItem label="申請" value={residenceData.applicationType} />
                <DetailItem label="現在の在留資格" value={getCurrentResidenceStatus(residenceData)} />
                <DetailItem label={t('applicant')} value={residenceData.applicant} />
                <DetailItem label={t('residenceCardNumber')} value={residenceData.residenceCardNumber} />
                <DetailItem label={t('residenceCardExpiry')} value={residenceData.residenceCardExpiry ? new Date(residenceData.residenceCardExpiry).toLocaleDateString('ja-JP') : 'N/A'} />
                <DetailItem label={t('passportNumber')} value={residenceData.passportNumber} />
                <DetailItem label={t('passportExpiry')} value={residenceData.passportExpiry ? new Date(residenceData.passportExpiry).toLocaleDateString('ja-JP') : 'N/A'} />
                <EditItem
                    label={t('startDate')}
                    name="startDate"
                    type="date"
                    value={editableResidenceDates.startDate}
                    onChange={handleResidenceDateChange}
                />
                <EditItem
                    label={t('endDate')}
                    name="endDate"
                    type="date"
                    value={editableResidenceDates.endDate}
                    onChange={handleResidenceDateChange}
                />
                <p className="mt-2 text-xs text-blue-800">
                    {!residenceData.id
                        ? '在留情報の基本項目はまだ未登録ですが、入職日・退職日だけ先に登録できます。その他の在留情報は residence アカウントで登録してください。'
                        : 'sales は入職日・退職日のみ編集できます。その他の在留情報は閲覧専用です。'}
                </p>

                {/* 更新履历显示 */}
                <div className="mt-6">
                    <h4 className="text-md font-semibold text-blue-900 mb-3">更新履歴</h4>
                    {residenceData.renewalEntries && residenceData.renewalEntries.length > 0 ? (
                        <div className="space-y-3">
                            {sortRenewalEntriesNewestFirst(residenceData.renewalEntries).map((entry, index) => (
                                <div key={entry.id || index} className="bg-white p-3 rounded border">
                                    <div className="grid grid-cols-3 gap-4 text-sm">
                                        <div>
                                            <span className="font-medium text-gray-600">更新日:</span>
                                            <div className="text-gray-900">
                                                {entry.renewalDate ? new Date(entry.renewalDate).toLocaleDateString('ja-JP') : 'N/A'}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-600">許可日:</span>
                                            <div className="text-gray-900">
                                                {entry.renewalGrantDate ? new Date(entry.renewalGrantDate).toLocaleDateString('ja-JP') : 'N/A'}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-600">受付番号:</span>
                                            <div className="text-gray-900">{entry.renewalNumber || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-600">在留資格:</span>
                                            <div className="text-gray-900">{entry.residenceStatus || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-600">申請:</span>
                                            <div className="text-gray-900">{entry.renewalApplicationType || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-600">提出方法:</span>
                                            <div className="text-gray-900">{entry.submissionMethod || ''}</div>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-600">提出(取次者):</span>
                                            <div className="text-gray-900">{entry.submissionAgent || 'N/A'}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">更新履歴はありません</p>
                    )}
                </div>
            </div>
        </div>
    );

    // 渲染可编辑的人才信息 - 完全匹配人材登録表单字段
    const renderEditableTalentInfo = () => {
        const isSpecifiedSkill2 = isSpecifiedSkill2Status(getCurrentResidenceStatus(residenceData));

        return (
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('talentInfo')}（{t('editable')}）</h3>
            <div className="flex">
                {/* 左侧个人信息 */}
                <div className="flex-1">
                    {/* 基本信息 */}
                    <EditItem label={t('fullName')} name="fullName" value={talentFormData.fullName} onChange={handleTalentChange} />
                <EditItem label={t('fullNameFurigana')} name="fullNameFurigana" value={talentFormData.fullNameFurigana} onChange={handleTalentChange} />
                <EditItem
                    label={t('managementNo')}
                    name="managementNumber"
                    value={talentFormData.managementNumber}
                    onChange={handleTalentChange}
                    inputMode="numeric"
                    pattern="\\d*"
                    maxLength={100}
                />

                <SelectItem
                    label={t('gender')}
                    name="gender"
                    value={talentFormData.gender}
                    onChange={handleTalentChange}
                    options={genderOptions}
                    optionType="gender"
                    t={t}
                    getTranslatedOption={getTranslatedOption}
                />
                <SelectItem
                    label={t('nationality')}
                    name="nationality"
                    value={talentFormData.nationality}
                    onChange={handleTalentChange}
                    options={nationalityOptions}
                    optionType="nationality"
                    t={t}
                    getTranslatedOption={getTranslatedOption}
                />
                <SelectItem
                    label={t('field')}
                    name="field"
                    value={showCustomField ? '他' : talentFormData.field}
                    onChange={handleTalentChange}
                    onInput={handleTalentChange}
                    options={fieldOptions}
                    optionType="field"
                    t={t}
                    getTranslatedOption={getTranslatedOption}
                />
                {showCustomField && (
                    <EditItem
                        label={t('customField')}
                        name="customField"
                        value={customFieldValue}
                        onChange={handleCustomFieldChange}
                    />
                )}
                <SelectItem
                    label={t('employmentStatus')}
                    name="employmentStatus"
                    value={talentFormData.employmentStatus}
                    onChange={handleTalentChange}
                    options={employmentStatusOptions}
                    optionType="employmentStatus"
                    t={t}
                    getTranslatedOption={getTranslatedOption}
                    required
                />

                <div className="grid grid-cols-5 gap-4 py-3 border-b border-gray-100">
                    <div className="col-span-2 text-sm font-medium text-gray-700 flex items-start pt-2">
                        {t('employmentPauseType')}履歴
                    </div>
                    <div className="col-span-3">
                        <div className="flex justify-end mb-2">
                            <button
                                type="button"
                                onClick={handleAddPauseRow}
                                className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
                            >
                                + 追加
                            </button>
                        </div>
                        {employmentPauseHistory.length === 0 ? (
                            <p className="text-xs text-gray-500">{t('none') || 'なし'}</p>
                        ) : (
                            <div className="space-y-2">
                                {employmentPauseHistory.map((row) => (
                                    <div key={row.id} className="grid grid-cols-4 gap-2 items-center">
                                        <select
                                            value={row.pauseType || '育休'}
                                            onChange={(e) => handlePauseRowChange(row.id, 'pauseType', e.target.value)}
                                            className="border-gray-300 rounded-md shadow-sm py-2 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        >
                                            {pauseTypeOptions.map(option => (
                                                <option key={option} value={option}>
                                                    {getTranslatedOption(option, 'employmentPauseType')}
                                                </option>
                                            ))}
                                        </select>
                                        <input
                                            type="date"
                                            min="1900-01-01"
                                            max="2099-12-31"
                                            value={normalizePauseDate(row.startDate)}
                                            onChange={(e) => handlePauseRowChange(row.id, 'startDate', e.target.value)}
                                            className="border-gray-300 rounded-md shadow-sm py-2 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                        <input
                                            type="date"
                                            min="1900-01-01"
                                            max="2099-12-31"
                                            value={normalizePauseDate(row.endDate)}
                                            onChange={(e) => handlePauseRowChange(row.id, 'endDate', e.target.value)}
                                            className="border-gray-300 rounded-md shadow-sm py-2 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleRemovePauseRow(row.id)}
                                            className="text-xs text-red-600 hover:text-red-800"
                                        >
                                            {t('delete') || '削除'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="text-xs text-gray-500 mt-2">{t('employmentPauseHint')}</div>
                    </div>
                </div>

                <EditItem
                    label={t('birthDate')}
                    name="dateOfBirth"
                    type="date"
                    value={talentFormData.dateOfBirth?.split('T')[0]}
                    onChange={handleTalentChange}
                />
                <EditItem label={t('phoneNumber')} name="phoneNumber" value={talentFormData.phoneNumber} onChange={handleTalentChange} />

                <div className="grid grid-cols-5 gap-4 py-3 border-b border-gray-100">
                    <label className="col-span-2 text-sm font-medium text-gray-700 flex items-center">{t('address')}</label>
                    <div className="col-span-3">
                        <JapanAddressSelector
                            name="address"
                            value={talentFormData.address}
                            onChange={handleTalentChange}
                        />
                    </div>
                </div>

                {/* 取引先情報 */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                    <h4 className="text-md font-semibold text-gray-700 mb-3">{t('clientManagement.title')}</h4>
                    <ClientSelectionFields
                        value={talentFormData}
                        onChange={handleClientSelectionChange}
                        variant="rows5"
                    />
                </div>

                {/* 費用情報 */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                    <h4 className="text-md font-semibold text-gray-700 mb-3">{t('costs')}</h4>
                    {isSpecifiedSkill2 ? (
                        <>
                            <div className="grid grid-cols-5 gap-4 py-3 border-b border-indigo-100 bg-indigo-50/60 px-3 rounded-md">
                                <div className="col-span-2 text-sm font-medium text-indigo-800 flex items-center">特定技能2号</div>
                                <div className="col-span-3 text-sm text-indigo-700">
                                    初期費用・管理費は統計対象外です。紹介費のみ登録できます。
                                </div>
                            </div>
                            <EditItem
                                label={t('introductionFee')}
                                name="introductionFee"
                                type="number"
                                value={talentFormData.introductionFee ?? ''}
                                onChange={handleTalentChange}
                            />
                        </>
                    ) : (
                        <>
                    <div className="grid grid-cols-5 gap-4 py-3 border-b border-gray-100">
                        <div className="col-span-2 text-sm font-medium text-gray-700 flex items-center">{t('initialCostNotCharged')}</div>
                        <div className="col-span-3">
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    name="initialCostNotCharged"
                                    checked={Boolean(talentFormData.initialCostNotCharged)}
                                    onChange={handleTalentChange}
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span>{t('noCharge')}</span>
                            </label>
                        </div>
                    </div>
                    <EditItem
                        label={t('initialCost')}
                        name="initialCost"
                        type="number"
                        value={talentFormData.initialCost ?? ''}
                        onChange={handleTalentChange}
                        disabled={Boolean(talentFormData.initialCostNotCharged)}
                    />
                    <div className="grid grid-cols-5 gap-4 py-3 border-b border-gray-100">
                        <div className="col-span-2 text-sm font-medium text-gray-700 flex items-center">{t('managementFeeNotCharged')}</div>
                        <div className="col-span-3">
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    name="managementFeeNotCharged"
                                    checked={Boolean(talentFormData.managementFeeNotCharged)}
                                    onChange={handleTalentChange}
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span>{t('noCharge')}</span>
                            </label>
                            {talentFormData.managementFeeNotCharged && (
                                <p className="text-xs text-gray-500 mt-1">{t('managementFeeNotChargedHint')}</p>
                            )}
                        </div>
                    </div>
                    <SelectItem
                        label={t('billingPlanType')}
                        name="billingPlanType"
                        value={talentFormData.billingPlanType || '通常'}
                        onChange={handleTalentChange}
                        options={billingPlanTypeOptions}
                        optionType="billingPlanType"
                        t={t}
                        getTranslatedOption={getTranslatedOption}
                        disabled={Boolean(talentFormData.managementFeeNotCharged)}
                    />

                    {!talentFormData.managementFeeNotCharged && (talentFormData.billingPlanType === '通常' || talentFormData.billingPlanType === '初期費+Nヶ月管理費' || !talentFormData.billingPlanType) && (
                        <EditItem label={t('managementFee')} name="managementFee" type="number" value={talentFormData.managementFee} onChange={handleTalentChange} />
                    )}

                    {!talentFormData.managementFeeNotCharged && talentFormData.billingPlanType === '通常' && (
                        <>
                            <EditItem label={t('firstMonthManagementFee')} name="firstMonthManagementFee" type="number" value={talentFormData.firstMonthManagementFee} onChange={handleTalentChange} />
                            <p className="text-xs text-gray-500 mt-1">{t('firstMonthManagementFeeHint')}</p>
                        </>
                    )}

                    {!talentFormData.managementFeeNotCharged && talentFormData.billingPlanType === '初期費+Nヶ月管理費' && (
                        <EditItem label={t('includedManagementMonths')} name="includedManagementMonths" type="number" value={talentFormData.includedManagementMonths ?? ''} onChange={handleTalentChange} />
                    )}

                    {!talentFormData.managementFeeNotCharged && talentFormData.billingPlanType === '段階管理費' && (
                        <>
                            <EditItem label={t('tier1Months')} name="tier1Months" type="number" value={talentFormData.tier1Months ?? ''} onChange={handleTalentChange} />
                            <EditItem label={t('tier1ManagementFee')} name="tier1ManagementFee" type="number" value={talentFormData.tier1ManagementFee ?? ''} onChange={handleTalentChange} />
                            <EditItem label={t('tier2Months')} name="tier2Months" type="number" value={talentFormData.tier2Months ?? ''} onChange={handleTalentChange} />
                            <EditItem label={t('tier2ManagementFee')} name="tier2ManagementFee" type="number" value={talentFormData.tier2ManagementFee ?? ''} onChange={handleTalentChange} />
                        </>
                    )}
                        </>
                    )}

                    <EditItem label={t('contractEndDate')} name="contractEndDate" type="date" value={talentFormData.contractEndDate?.split('T')[0]} onChange={handleTalentChange} />

                    {!isSpecifiedSkill2 && (
                    <div className="mt-6 pt-4 border-t border-gray-200">
                        <h4 className="text-md font-semibold text-gray-700 mb-3">{t('billingTrend')}</h4>
                        <TalentBillingChart talentId={talent?.id} />
                    </div>
                    )}
                </div>
                </div>

                {/* 右侧照片区域 - 与姓名字段对齐 */}
                <div className="ml-6 flex flex-col items-center space-y-2">
                    {/* 写真プレビューエリア - 3cm x 4cm */}
                    <div
                        className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden"
                        style={{ width: '113px', height: '151px' }}
                    >
                        {photoPreviewUrl ? (
                            <img
                                src={photoPreviewUrl}
                                alt="人材写真"
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="text-center p-2">
                                <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <p className="text-xs text-gray-500">写真なし</p>
                            </div>
                        )}
                    </div>

                    {/* Size information */}
                    <p className="text-xs text-gray-600">3cm × 4cm</p>

                    {/* Photo upload and download buttons */}
                    <div className="flex space-x-2">
                        <label className="cursor-pointer">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoChange}
                                className="hidden"
                            />
                            <span className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors">
                                アップロード
                            </span>
                        </label>

                        {existingPhoto && (
                            <button
                                type="button"
                                onClick={() => attachmentPreview.openPreview(existingPhoto.id, existingPhoto.fileName || 'photo.jpg', existingPhoto.fileType)}
                                className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors"
                            >
                                {t('viewOnline')}
                            </button>
                        )}

                        {photoPreviewUrl && (
                            <button
                                type="button"
                                onClick={handleDownloadPhoto}
                                className="text-xs bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 transition-colors"
                            >
                                ダウンロード
                            </button>
                        )}
                    </div>

                    {(photoPreviewUrl || existingPhoto) && (
                        <button
                            type="button"
                            onClick={handleDeletePhoto}
                            className="text-xs text-red-600 hover:text-red-800"
                        >
                            削除
                        </button>
                    )}
                </div>
            </div>
        </div>
        );
    };

    const renderAttachments = () => (
        <div className="md:col-span-3 border-t pt-4 mt-2">
            <h4 className="font-semibold text-gray-800">{t('attachments')}</h4>

            {/* ファイル選択と追加ボタン */}
            <div className="mb-4 mt-2">
                <div className="flex items-center gap-2">
                    <label className="inline-flex cursor-pointer items-center justify-center rounded-full bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-600 hover:bg-indigo-100">
                        <input
                            id="file-input-sales"
                            type="file"
                            multiple
                            onChange={handleFileChange}
                            className="hidden"
                        />
                        {t('selectFiles')}
                    </label>
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-500">
                        {selectedFiles.length > 0 ? t('selectedFilesCount', { count: selectedFiles.length }) : t('noFilesSelected')}
                    </span>
                    <button
                        type="button"
                        onClick={handleAddFiles}
                        disabled={selectedFiles.length === 0}
                        className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                        追加
                    </button>
                </div>
                {selectedFiles.length > 0 && (
                    <p className="text-xs text-gray-600 mt-1">選択中: {selectedFiles.length}個のファイル</p>
                )}
            </div>

            {/* アップロード予定ファイル一覧 */}
            {filesToUpload.length > 0 && (
                <div className="mb-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">追加予定のファイル:</h5>
                    <ul className="space-y-1">
                        {filesToUpload.map((file, index) => (
                            <li key={index} className="flex items-center justify-between py-2 px-3 bg-yellow-50 border border-yellow-200 rounded">
                                <div className="flex items-center">
                                    <span className="text-2xl mr-2">📎</span>
                                    <span className="text-sm text-gray-700">{file.name}</span>
                                    <span className="text-xs text-gray-500 ml-2">({(file.size / 1024).toFixed(1)} KB)</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleRemovePendingFile(index)}
                                    className="text-red-500 hover:text-red-700 text-sm"
                                >
                                    削除
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* アップロード済み添付ファイル一覧 */}
            <h5 className="text-sm font-medium text-gray-700 mb-2">アップロード済みファイル:</h5>
            {existingAttachments.length > 0 ? (
                <ul className="mt-2">
                    {existingAttachments.map(att => (
                        <li key={att.id} className="flex items-center justify-between py-2 border-b">
                            <div className="flex items-center">
                                {isImage(att.fileType) && thumbnailUrls[att.id] ? (
                                    <img src={thumbnailUrls[att.id]} alt={att.fileName} className="w-16 h-16 object-cover mr-4 rounded" />
                                ) : (
                                    <span className="text-4xl mr-4">📄</span>
                                )}
                                <span>{att.fileName}</span>
                            </div>
                            <div>
                                <button type="button" onClick={() => attachmentPreview.openPreview(att.id, att.fileName, att.fileType)} className="text-blue-500 hover:underline mr-4">{t('viewOnline')}</button>
                                <button type="button" onClick={() => handleDownload(att.id, att.fileName)} className="text-blue-500 hover:underline mr-4">{t('download')}</button>
                                <button type="button" onClick={() => handleDeleteAttachment(att.id)} className="text-red-500 hover:underline">{t('delete')}</button>
                            </div>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-sm text-gray-500">{t('none')}</p>
            )}
        </div>
    );

    return (
        <>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl">
                <div className="p-6 border-b">
                    <h2 className="text-2xl font-bold">{talent?.fullName} - 人材・在留情報</h2>
                </div>
                <div className="p-6 max-h-[70vh] overflow-y-auto">
                    {isLoading ? (
                        <p>{t('loading')}</p>
                    ) : (
                        <>
                            {renderResidenceInfo()}
                            {renderEditableTalentInfo()}
                            {renderAttachments()}
                        </>
                    )}
                </div>
                {error && <p className="px-6 pb-4 text-sm text-red-600">{error}</p>}
                <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-4">
                    <button onClick={handleSaveTalentInfo} disabled={isLoading} className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:bg-indigo-400">
                        {isLoading ? t('saving') : '変更を保存'}
                    </button>
                    <button onClick={onClose} className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50">
                        {t('close')}
                    </button>
                </div>
            </div>
        </div>
        <AttachmentPreviewModal
            previewState={attachmentPreview.previewState}
            onClose={attachmentPreview.closePreview}
        />
        </>
    );
}
